/**
 * Pure billing plan definitions shared by checkout, the Stripe webhook, and
 * entitlement checks. No SDK imports here so this stays unit-testable.
 *
 * Prices live in ./planPricebook.ts. Each offered plan/cycle combination
 * must exist as an active Price in the Stripe dashboard with its `lookup_key`
 * set to the values below.
 */

/**
 * The account-level plan `payments.plan` records. `pro` is any paid Stripe
 * subscription; `lifetime` is a hand-granted plan that subscription churn
 * never changes. An account with neither has no plan field at all: its
 * access comes from its trial or subscription status alone.
 */
export type SubscriptionPlanType = "pro" | "lifetime";

/**
 * The value accounts carried before Smart Manager removed its free plan
 * (2026-10-20). It grants nothing: readers normalize it to "no plan" with
 * `normalizeSubscriptionPlan`, and nothing writes it anymore. Rows may still
 * hold it until `accountEntitlements:clearLegacyFreePlans` has run.
 */
export const LEGACY_FREE_PLAN = "free";
export type LegacyFreePlan = typeof LEGACY_FREE_PLAN;

/** What a stored `payments.plan` may hold: a current plan, or the legacy value. */
export type StoredSubscriptionPlan = SubscriptionPlanType | LegacyFreePlan;

/** A stored plan as the current plan it grants, or undefined for none. */
export function normalizeSubscriptionPlan(value: unknown): SubscriptionPlanType | undefined {
  return value === "pro" || value === "lifetime" ? value : undefined;
}

/**
 * Ordered from the fewest to the most features. `vault` (Vault) is the shell's
 * paid tier: memory, database and network as one bundle, answered by the
 * user's own agent. `executive` (Professional) adds the hosted orchestrator and hosted
 * voice; `business` (Enterprise) adds the industry agents and the workplace
 * focus.
 */
export type BillingPlanKey = "vault" | "executive" | "business";
export type BillingCycle = "monthly" | "yearly";

export const BILLING_PLAN_KEYS: readonly BillingPlanKey[] = ["vault", "executive", "business"];

/**
 * What people see each plan called. The keys stay as stored in Stripe and on
 * accounts; only these names are user-facing. They are product names, so
 * they are shown as written in every language.
 */
export const BILLING_PLAN_NAMES = {
  vault: "Vault",
  executive: "Professional",
  business: "Enterprise",
} as const satisfies Readonly<Record<BillingPlanKey, string>>;
export const BILLING_CYCLES: readonly BillingCycle[] = ["monthly", "yearly"];

export const STRIPE_PRICE_LOOKUP_KEYS: Record<BillingPlanKey, Record<BillingCycle, string>> = {
  vault: { monthly: "vault_monthly", yearly: "vault_yearly" },
  executive: { monthly: "executive_monthly", yearly: "executive_yearly" },
  business: { monthly: "business_monthly", yearly: "business_yearly" },
};

export { BILLING_PRICE_CENTS } from "./planPricebook";

export function isBillingPlanKey(value: unknown): value is BillingPlanKey {
  return typeof value === "string" && (BILLING_PLAN_KEYS as readonly string[]).includes(value);
}

export function isBillingCycle(value: unknown): value is BillingCycle {
  return typeof value === "string" && (BILLING_CYCLES as readonly string[]).includes(value);
}

export function getPriceLookupKey(plan: BillingPlanKey, cycle: BillingCycle): string {
  return STRIPE_PRICE_LOOKUP_KEYS[plan][cycle];
}

/**
 * Only terminal Stripe subscriptions may be replaced with a new checkout.
 * All other states still represent a live billing object that should be
 * recovered or changed through the customer portal, not duplicated.
 */
export function blocksNewSubscriptionCheckout(status: string | null | undefined): boolean {
  const normalized = String(status || "").trim().toLowerCase();
  return Boolean(normalized) && normalized !== "canceled" && normalized !== "incomplete_expired";
}

export type BillingActionState = {
  canStartCheckout: boolean;
  canManageSubscription: boolean;
  needsSupport: boolean;
};

/**
 * Keep Billing Settings aligned with the server checkout boundary. A live
 * Stripe subscription must be recovered in the portal instead of sending the
 * user into a checkout that the server will reject. Missing ownership fields
 * are surfaced as an account-repair state rather than offering either action.
 */
export function getBillingActionState({
  plan,
  subscriptionStatus,
  customerId,
  subscriptionId,
}: {
  plan?: StoredSubscriptionPlan | string | null;
  subscriptionStatus: string | null | undefined;
  customerId: string | null | undefined;
  subscriptionId: string | null | undefined;
}): BillingActionState {
  const normalizedStatus = String(subscriptionStatus || "").trim().toLowerCase();
  const hasCustomer = Boolean(String(customerId || "").trim());
  const hasSubscription = Boolean(String(subscriptionId || "").trim());
  const isLifetime = plan === "lifetime";
  // New accounts use `trialing` for the app-owned seven-day trial, without a
  // Stripe customer or subscription. That state is eligible for its first
  // checkout; a Stripe-owned trial always has billing identifiers and must be
  // recovered through the portal instead.
  const isLocalTrial = normalizedStatus === "trialing" && !hasCustomer && !hasSubscription;
  const hasExplicitTerminalStatus = normalizedStatus === "canceled"
    || normalizedStatus === "incomplete_expired";
  const hasLiveSubscription = (!isLocalTrial && normalizedStatus !== "none" && blocksNewSubscriptionCheckout(normalizedStatus))
    || (hasSubscription && !hasExplicitTerminalStatus);
  const needsSupport = !hasCustomer && (hasLiveSubscription || hasSubscription);
  const canManageSubscription = hasCustomer && hasLiveSubscription;

  return {
    canStartCheckout: !isLifetime && !canManageSubscription && !needsSupport,
    canManageSubscription,
    needsSupport,
  };
}

/** Stripe may create a paid Checkout subscription in either of these states. */
export function isSuccessfulCheckoutSubscriptionStatus(status: unknown): boolean {
  return status === "active" || status === "trialing";
}

export type VerifiedCheckoutSubscriptionShape = {
  subscriptionStatus?: unknown;
  subscriptionId?: unknown;
  customerId?: unknown;
  stripeCustomerId?: unknown;
};

/**
 * A local Smart Manager trial also uses the `trialing` status, but it has no
 * Stripe subscription ownership. Client checkout-completion effects must
 * require the verified Stripe identifiers as well as the status so an expired
 * local trial cannot be mistaken for a newly completed checkout.
 */
export function hasVerifiedCheckoutSubscription(
  payments: VerifiedCheckoutSubscriptionShape | null | undefined,
): boolean {
  const subscriptionId = String(payments?.subscriptionId || "").trim();
  const customerId = String(
    payments?.customerId || payments?.stripeCustomerId || "",
  ).trim();

  return isSuccessfulCheckoutSubscriptionStatus(payments?.subscriptionStatus)
    && Boolean(subscriptionId)
    && Boolean(customerId);
}

/**
 * Subscription statuses under which a subscriber keeps access. `past_due` is
 * included to honor the Terms' failed-payment grace period while Stripe's
 * smart retries run; once Stripe gives up the subscription transitions to
 * `canceled`/`unpaid` and access ends.
 */
export const ENTITLED_SUBSCRIPTION_STATUSES: ReadonlySet<string> = new Set([
  "active",
  "past_due",
]);

/**
 * Map a Stripe subscription status onto the app's internal plan field, or
 * undefined when the subscription grants no plan (the writer then leaves
 * `plan` off the record). A `lifetime` plan is never downgraded by
 * subscription churn.
 */
export function planForSubscriptionStatus(
  status: string,
  existingPlan?: StoredSubscriptionPlan | string
): SubscriptionPlanType | undefined {
  if (existingPlan === "lifetime") return "lifetime";
  if (status === "active" || status === "trialing" || status === "past_due") return "pro";
  return undefined;
}

/**
 * `payments` with `plan` set to `plan`, or with no `plan` key at all when
 * `plan` is undefined. A spread such as `{ ...payments, plan }` would keep a
 * stale `pro` or legacy `free` value instead of clearing it.
 */
export function withSubscriptionPlan<T extends object>(
  payments: T,
  plan: SubscriptionPlanType | undefined,
): Omit<T, "plan"> & { plan?: SubscriptionPlanType } {
  const rest = { ...payments } as Omit<T, "plan"> & { plan?: unknown };
  delete rest.plan;
  return plan === undefined ? (rest as Omit<T, "plan">) : { ...rest, plan };
}
